import { components, paths } from "@/api-schema";
import {
  PathsV1UserSearchPostParametersQueryExtend,
  PathsV1UserSearchPostParametersQueryOrderByDirection,
  PathsV1UserSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useUserSearchQuery } from "@/common/api/user/use-user-search.query";
import { getUserDetailPath } from "@/common/helpers/get-user-detail-path";
import { normalizePhoneSearchTerm } from "@/common/helpers/normalize-phone-search-term";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { CustomSelectionAction, DataTableRow, TableFeatures } from "@/components/data-table/types";
import { RowAction } from "@/components/ui/table-row-actions";
import { useCreateDraftMail } from "@/modules/phonebook/_helpers/use-create-draft-mail";
import { MailIcon, PhoneCallIcon, SmartphoneIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { phonebookTableDefaultColumns } from "./phonebook-table-columns";

type User = components["schemas"]["User"] & RowWithIdentifier;
type UserSorting = NonNullable<paths["/v1/user/search"]["post"]["parameters"]["query"]["orderBy"]>;
type UserQueryParams = paths["/v1/user/search"]["post"]["parameters"]["query"];
type UserRequestBody = paths["/v1/user/search"]["post"]["requestBody"]["content"]["application/json"];
type UserExtend = NonNullable<paths["/v1/user/search"]["post"]["parameters"]["query"]["extend"]>;

const DEFAULT_EXTEND: UserExtend = [
  PathsV1UserSearchPostParametersQueryExtend.Address,
  PathsV1UserSearchPostParametersQueryExtend.MainBranch,
  PathsV1UserSearchPostParametersQueryExtend.MainBranch_Company,
  PathsV1UserSearchPostParametersQueryExtend.MainBranch_Brands,
  PathsV1UserSearchPostParametersQueryExtend.MainBranch_Address,
  PathsV1UserSearchPostParametersQueryExtend.Departments,
  PathsV1UserSearchPostParametersQueryExtend.Occupations,
] as const;

const DEFAULT_COLUMNS = [
  phonebookTableDefaultColumns.columnAvatar,
  phonebookTableDefaultColumns.columnName,
  phonebookTableDefaultColumns.columnNameAndContact,
  phonebookTableDefaultColumns.columnBranch,
  phonebookTableDefaultColumns.columnContact,
  phonebookTableDefaultColumns.columnDepartment,
];

const PHONEBOOK_TABLE_CONFIG: PredefinedTableConfig<UserSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1UserSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1UserSearchPostParametersQueryOrderByField.LastName,
    },
    {
      direction: PathsV1UserSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1UserSearchPostParametersQueryOrderByField.FirstName,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: "common:SearchUser",
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: true,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: false,
    showDelete: false,
    viewLabel: "phonebook:ViewProfile",
  } as const,
  sortingMapping: {
    name: ["lastName", "firstName"],
  } as const,
} as const;

function usePhonebookSearchQuery(args: Parameters<typeof useUserSearchQuery>[0] = {}) {
  const parameters = args.options?.parameters;

  return useUserSearchQuery({
    ...args,
    options: args.options && {
      ...args.options,
      parameters: parameters && {
        ...parameters,
        search: parameters.search && normalizePhoneSearchTerm(parameters.search),
      },
    },
  });
}

const PHONEBOOK_TABLE_QUERY_HOOKS: DataFetchHooks<User, UserQueryParams, UserRequestBody> = {
  useSearchQuery: usePhonebookSearchQuery,
} as const;

export interface PhonebookTableProps extends BasePredefinedTableProps<
  User,
  UserSorting,
  UserQueryParams,
  UserRequestBody
> {
  extend?: UserExtend;
}

const BasePhonebookTable = createBasePredefinedTable({
  config: PHONEBOOK_TABLE_CONFIG,
  queryHooks: PHONEBOOK_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function PhonebookTable(props: PhonebookTableProps) {
  const [_, navigate] = useLocation();
  const { t } = useTranslation(["phonebook", "common"]);
  const [selection, setSelection] = useState<User[]>([]);
  const { createDraftMail } = useCreateDraftMail();

  function navigateToUserProfile(row: User) {
    navigate(getUserDetailPath(row.uuid));
  }

  const handleSelectionChange = useCallback((selectedUsers: User[] | string[]) => {
    setSelection(selectedUsers as User[]);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelection([]);
  }, []);

  const handleRowClick = useCallback((user: User) => {
    setSelection((prev) => {
      const isSelected = prev.some((selected) => selected.uuid === user.uuid);
      return isSelected ? prev.filter((selected) => selected.uuid !== user.uuid) : [...prev, user];
    });
  }, []);

  const handleCreateDraftMail = useCallback(
    async (selectedUsers: User[] | string[]): Promise<void> => {
      const allUsers = selectedUsers as User[];
      await createDraftMail(allUsers);
    },
    [createDraftMail],
  );

  const customSelectionActions: CustomSelectionAction<User>[] = useMemo(
    () => [
      {
        action: handleCreateDraftMail,
        icon: MailIcon,
        label: t("phonebook:CreateEmail"),
      },
    ],
    [handleCreateDraftMail, t],
  );

  const customRowActions = useCallback(
    (row: DataTableRow<User>) => {
      const actions: RowAction[] = [];
      const { email, telephone, mobile } = row.original;

      if (email) {
        actions.push({
          label: t("phonebook:SendEmail"),
          icon: <MailIcon className="h-4 w-4" />,
          onClick: () => {
            window.location.href = `mailto:${email}`;
          },
        });
      }

      if (telephone) {
        actions.push({
          label: t("phonebook:PhoneCall"),
          icon: <PhoneCallIcon className="h-4 w-4" />,
          onClick: () => {
            window.location.href = `tel:${telephone}`;
          },
        });
      }

      if (mobile) {
        actions.push({
          label: t("phonebook:MobileCall"),
          icon: <SmartphoneIcon className="h-4 w-4" />,
          onClick: () => {
            window.location.href = `tel:${mobile}`;
          },
        });
      }

      return actions;
    },
    [t],
  );

  const {
    name = "phonebook-table",
    extend = DEFAULT_EXTEND,
    onRowClick = selection.length > 0 ? handleRowClick : undefined,
    onRowNavigate = (row: User) => getUserDetailPath(row.uuid),
    onView = navigateToUserProfile,
    ...restProps
  } = props;

  return (
    <BasePhonebookTable
      searchPlaceholder={t("common:SearchUser")}
      name={name}
      extend={extend}
      onRowClick={onRowClick}
      onRowNavigate={onRowNavigate}
      onView={onView}
      features={{
        multiSelection: true,
        pagination: true,
        search: true,
        expansion: false,
      }}
      onSelectionChange={handleSelectionChange}
      onClearSelection={handleClearSelection}
      selection={selection}
      selectionMode="object"
      customRowActions={customRowActions}
      customSelectionActions={customSelectionActions}
      {...restProps}
    />
  );
}
